import { useCallback, useEffect, useState } from 'react';
import type { PlayerPreferences } from '@rummy/protocol';

export interface Settings {
  theme: 'system' | 'light' | 'dark';
  /** Visual treatment for the in-game card room. */
  tableSkin: 'jade' | 'midnight' | 'noir' | 'sunset' | 'seasonal';
  /** The player's signature card-back at the table. */
  cardBack: 'club' | 'aurora' | 'ruby' | 'ivory';
  /** Personal accent for the player's own table controls and monogram. */
  playerAccent: 'violet' | 'rose' | 'aqua' | 'gold';
  /** Card sizing is an accessibility preference; landscape still protects board space. */
  cardSize: 'compact' | 'comfortable' | 'large';
  highContrast: boolean;
  /**
   * How much the table moves.
   *
   * A budget, not a switch. This used to be `reduceEffects: boolean`, which set
   * `animation: none` on every element — so anyone who wanted a calmer table
   * had to choose between all of the motion and a static page. Three tiers:
   *
   * - `full`    — everything, including ambient light and card material.
   * - `reduced` — the motion that *says* something (a card moving between two
   *               places, a meld closing, a turn arriving) and nothing that is
   *               only atmosphere.
   * - `none`    — opacity only, the `prefers-reduced-motion` contract.
   *
   * `prefers-reduced-motion: reduce` still forces the floor independently, so
   * the OS setting is honoured whatever is chosen here.
   */
  motion: 'full' | 'reduced' | 'none';
  handedness: 'right' | 'left';
  /** §6.5 — a four-colour deck is an accessibility feature that many strong
   *  players prefer regardless. */
  deck: 'two' | 'four';
  /** Card slide, place and shuffle. Atmosphere — off by default (§6.5). */
  sound: boolean;
  /**
   * The chime when the turn reaches you. On by default, and deliberately
   * separate from `sound`.
   *
   * §6.5 puts the turn chime in the same box as the card sounds, which meant it
   * inherited their default and nobody ever heard it: the one sound on the table
   * that carries information was switched off to keep the table quiet. The card
   * sounds are atmosphere and stay opt-in; missing your turn to the timer
   * because you looked away is not an atmosphere problem.
   */
  turnAlert: boolean;
  /** §6.3 — confirm-on-discard is off by default, on as a setting. */
  confirmDiscard: boolean;
  /** Fewer transitions and an automatic sort after drawing, for players who
   * already know the table and want to move through hands quickly. */
  quickPlay: boolean;
  /** A dense controls-and-information layout for regular, experienced players.
   * It deliberately keeps the table's visual effects; it only moves incidental
   * controls out of the active decision path. */
  expertMode: boolean;
  /** Public cards and public opponent actions, recorded locally for the hand. */
  showTableIntel: boolean;
  /** Meld counts and optional in-game coaching. Experienced players can keep
   * the table focused on cards and actions alone. */
  showGameplayGuidance: boolean;
  /** A backgrounded tab mid-session otherwise loses a turn silently to the
   *  timer. Off by default: it needs an explicit Notification permission
   *  grant, and device comfort controls stay local, not account-synced. */
  turnNotifications: boolean;
}

const DEFAULTS: Settings = {
  // The club-dark theme is the brand; daylight is the opt-in.
  theme: 'dark',
  tableSkin: 'jade',
  cardBack: 'club',
  playerAccent: 'violet',
  cardSize: 'comfortable',
  highContrast: false,
  motion: 'full',
  handedness: 'right',
  deck: 'two',
  sound: false,
  turnAlert: true,
  confirmDiscard: false,
  quickPlay: false,
  expertMode: false,
  showTableIntel: false,
  showGameplayGuidance: true,
  turnNotifications: false,
};

/**
 * How much the table explains itself.
 *
 * `quickPlay`, `expertMode` and `showGameplayGuidance` are three switches
 * answering one question — how much does this player already know — and a
 * settings panel that asks it three times has made the reader do the mapping.
 * The preset asks once; the individual switches stay for anyone who wants a
 * combination the presets do not offer.
 */
export type Experience = 'learning' | 'regular' | 'expert';

export const EXPERIENCE_PRESETS: Record<Experience, Partial<Settings>> = {
  learning: {
    showGameplayGuidance: true,
    expertMode: false,
    quickPlay: false,
    // A misplaced discard is the mistake a new player actually makes, and the
    // only one on the table that cannot be undone.
    confirmDiscard: true,
    showTableIntel: false,
  },
  regular: {
    showGameplayGuidance: true,
    expertMode: false,
    quickPlay: false,
    confirmDiscard: false,
    showTableIntel: false,
  },
  expert: {
    showGameplayGuidance: false,
    expertMode: true,
    quickPlay: true,
    confirmDiscard: false,
    showTableIntel: true,
  },
};

/** Which preset these settings match, or `null` for a hand-rolled combination. */
export function experienceOf(settings: Settings): Experience | null {
  for (const [name, preset] of Object.entries(EXPERIENCE_PRESETS)) {
    const matches = Object.entries(preset).every(
      ([key, value]) => settings[key as keyof Settings] === value,
    );
    if (matches) return name as Experience;
  }
  return null;
}

const KEY = 'rummy.settings';

function read(): Settings {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Settings> & {
      reduceEffects?: boolean;
    };
    // `reduceEffects` was a boolean before motion became a budget. Someone who
    // had turned it on asked for less movement, not for none of it.
    if (stored.motion === undefined && stored.reduceEffects !== undefined) {
      stored.motion = stored.reduceEffects ? 'reduced' : 'full';
    }
    delete stored.reduceEffects;
    return { ...DEFAULTS, ...stored };
  } catch {
    return DEFAULTS;
  }
}

export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [settings, setSettings] = useState<Settings>(read);

  useEffect(() => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = settings.theme === 'dark' || (settings.theme === 'system' && prefersDark.matches);
      root.dataset['theme'] = dark ? 'dark' : 'light';
    };
    apply();
    prefersDark.addEventListener('change', apply);
    root.dataset['deck'] = settings.deck === 'four' ? 'four-colour' : 'two-colour';
    root.dataset['tableSkin'] = settings.tableSkin === 'seasonal' ? seasonalSkin() : settings.tableSkin;
    root.dataset['cardBack'] = settings.cardBack;
    root.dataset['playerAccent'] = settings.playerAccent;
    root.dataset['cardSize'] = settings.cardSize;
    root.dataset['highContrast'] = settings.highContrast ? 'true' : 'false';
    root.dataset['motion'] = settings.motion;
    root.dataset['handedness'] = settings.handedness;
    root.dataset['quickPlay'] = settings.quickPlay ? 'true' : 'false';
    root.dataset['expertMode'] = settings.expertMode ? 'true' : 'false';
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      /* private browsing */
    }
    return () => prefersDark.removeEventListener('change', apply);
  }, [settings]);

  const update = useCallback(
    (patch: Partial<Settings>) => setSettings((s) => ({ ...s, ...patch })),
    [],
  );

  return [settings, update];
}

/** Only visual/game-legibility choices travel with an account. Device comfort
 * controls remain local because they describe this device and environment. */
export function playerPreferences(settings: Settings): PlayerPreferences {
  return {
    tableSkin: settings.tableSkin,
    cardBack: settings.cardBack,
    playerAccent: settings.playerAccent,
    cardSize: settings.cardSize,
    deck: settings.deck,
  };
}

export function preferencesPatch(preferences: PlayerPreferences): Partial<Settings> {
  return {
    tableSkin: preferences.tableSkin,
    cardBack: preferences.cardBack,
    playerAccent: preferences.playerAccent,
    cardSize: preferences.cardSize,
    deck: preferences.deck,
  };
}

/**
 * Four quiet seasonal atmospheres — no dated event artwork, no remote assets.
 *
 * These used to map onto the four skins a player could already choose, which
 * meant picking "Seasonal club" in spring handed you the jade table you were
 * already looking at: a setting that visibly did nothing. They are now their
 * own palettes (`[data-table-skin='spring']` and friends in `table.css`), built
 * the same way as the rest — gradients over the same lounge backdrop.
 */
function seasonalSkin(): string {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 8) return 'monsoon';
  if (month >= 9 && month <= 10) return 'festival';
  return 'winter';
}
